import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  hue: number;
  type: 'red' | 'cyan' | 'gold';
}

interface DataStream {
  x: number;
  y: number;
  speed: number;
  length: number;
  chars: string[];
  opacity: number;
  hue: number;
}

interface Pulse {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
}

export const InteractiveBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const streamsRef = useRef<DataStream[]>([]);
  const pulsesRef = useRef<Pulse[]>([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let resizeTimeout: number | undefined;

    const resize = () => {
      // Batch DOM reads first to avoid forced reflow
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Use requestAnimationFrame to batch DOM writes
      requestAnimationFrame(() => {
        canvas.width = width;
        canvas.height = height;
        initParticles();
        initDataStreams();
      });
    };

    const debouncedResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = window.setTimeout(resize, 100);
    };

    const initParticles = () => {
      const count = Math.floor((canvas.width * canvas.height) / 12000);
      const types: Array<'red' | 'cyan' | 'gold'> = ['red', 'red', 'red', 'cyan', 'gold'];
      
      particlesRef.current = Array.from({ length: count }, () => {
        const type = types[Math.floor(Math.random() * types.length)];
        let hue = 0;
        if (type === 'red') hue = Math.random() * 20 - 10;
        else if (type === 'cyan') hue = 180 + Math.random() * 20;
        else if (type === 'gold') hue = 45 + Math.random() * 10;
        
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.4 + 0.1,
          hue,
          type,
        };
      });
    };

    const initDataStreams = () => {
      const count = Math.floor(canvas.width / 80);
      const chars = '01アイウエオカキクケコ<>{}[]$/\\*&@#';
      
      streamsRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        speed: Math.random() * 1.5 + 0.5,
        length: Math.floor(Math.random() * 25 + 8),
        chars: Array.from({ length: 30 }, () => chars[Math.floor(Math.random() * chars.length)]),
        opacity: Math.random() * 0.2 + 0.05,
        hue: Math.random() > 0.7 ? 180 : 0, // Mix red and cyan streams
      }));
    };

    const createPulse = (x: number, y: number) => {
      pulsesRef.current.push({
        x,
        y,
        radius: 0,
        maxRadius: 150 + Math.random() * 100,
        opacity: 0.3,
      });
    };

    const drawCircuitLines = (time: number) => {
      const gridSize = 80;
      ctx.lineWidth = 0.5;
      
      for (let x = 0; x < canvas.width; x += gridSize) {
        for (let y = 0; y < canvas.height; y += gridSize) {
          const distFromMouse = Math.sqrt(
            Math.pow(mouseRef.current.x - x, 2) + 
            Math.pow(mouseRef.current.y - y, 2)
          );
          
          const proximity = Math.max(0, 1 - distFromMouse / 300);
          const baseOpacity = 0.02 + proximity * 0.08;
          
          // Animate node glow
          const pulse = Math.sin(time * 0.002 + x * 0.01 + y * 0.01) * 0.5 + 0.5;
          
          // Draw node
          ctx.fillStyle = `hsla(0, 75%, 50%, ${baseOpacity + pulse * 0.03})`;
          ctx.beginPath();
          ctx.arc(x, y, 2 + proximity * 2, 0, Math.PI * 2);
          ctx.fill();
          
          // Draw horizontal line
          if (x + gridSize < canvas.width) {
            const gradient = ctx.createLinearGradient(x, y, x + gridSize, y);
            gradient.addColorStop(0, `hsla(0, 75%, 50%, ${baseOpacity * 0.5})`);
            gradient.addColorStop(0.5, `hsla(0, 75%, 50%, ${baseOpacity})`);
            gradient.addColorStop(1, `hsla(0, 75%, 50%, ${baseOpacity * 0.5})`);
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(x + 4, y);
            ctx.lineTo(x + gridSize - 4, y);
            ctx.stroke();
          }
          
          // Draw vertical line
          if (y + gridSize < canvas.height) {
            const gradient = ctx.createLinearGradient(x, y, x, y + gridSize);
            gradient.addColorStop(0, `hsla(0, 75%, 50%, ${baseOpacity * 0.5})`);
            gradient.addColorStop(0.5, `hsla(0, 75%, 50%, ${baseOpacity})`);
            gradient.addColorStop(1, `hsla(0, 75%, 50%, ${baseOpacity * 0.5})`);
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(x, y + 4);
            ctx.lineTo(x, y + gridSize - 4);
            ctx.stroke();
          }
        }
      }
    };

    const drawDataStreams = () => {
      streamsRef.current.forEach(stream => {
        const charHeight = 14;
        stream.chars.forEach((char, i) => {
          const y = stream.y + i * charHeight;
          if (y > 0 && y < canvas.height) {
            const fade = i / stream.chars.length;
            const saturation = stream.hue === 180 ? 80 : 75;
            ctx.fillStyle = `hsla(${stream.hue}, ${saturation}%, ${50 + fade * 20}%, ${stream.opacity * (1 - fade * 0.8)})`;
            ctx.font = '11px "IBM Plex Mono", monospace';
            ctx.fillText(char, stream.x, y);
          }
        });
        
        stream.y += stream.speed;
        if (stream.y > canvas.height + stream.length * 14) {
          stream.y = -stream.length * 14;
          stream.x = Math.random() * canvas.width;
        }
      });
    };

    const drawParticles = () => {
      particlesRef.current.forEach(p => {
        // Mouse interaction
        const dx = mouseRef.current.x - p.x;
        const dy = mouseRef.current.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 180) {
          const force = (180 - dist) / 180;
          p.vx -= (dx / dist) * force * 0.015;
          p.vy -= (dy / dist) * force * 0.015;
        }

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Damping
        p.vx *= 0.995;
        p.vy *= 0.995;

        // Boundaries
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Draw glow
        const saturation = p.type === 'cyan' ? 80 : p.type === 'gold' ? 90 : 75;
        const lightness = p.type === 'gold' ? 55 : 50;
        
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
        glow.addColorStop(0, `hsla(${p.hue}, ${saturation}%, ${lightness}%, ${p.opacity})`);
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Draw core
        ctx.fillStyle = `hsla(${p.hue}, ${saturation}%, ${lightness + 10}%, ${p.opacity * 1.5})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw connections
      particlesRef.current.forEach((p1, i) => {
        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 100) {
            const avgHue = (p1.hue + p2.hue) / 2;
            ctx.strokeStyle = `hsla(${avgHue}, 75%, 50%, ${0.08 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });
    };

    const drawPulses = () => {
      pulsesRef.current = pulsesRef.current.filter(pulse => {
        pulse.radius += 3;
        pulse.opacity = 0.3 * (1 - pulse.radius / pulse.maxRadius);
        
        if (pulse.opacity <= 0) return false;
        
        ctx.strokeStyle = `hsla(0, 75%, 50%, ${pulse.opacity})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
        ctx.stroke();
        
        return pulse.radius < pulse.maxRadius;
      });
    };

    const drawScanLine = (time: number) => {
      const y = (time * 0.02) % canvas.height;
      const gradient = ctx.createLinearGradient(0, y - 30, 0, y + 30);
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.5, 'hsla(0, 75%, 50%, 0.02)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, y - 30, canvas.width, 60);
    };

    const drawVignette = () => {
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) * 0.6
      );
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.7, 'hsla(0, 5%, 2%, 0.3)');
      gradient.addColorStop(1, 'hsla(0, 5%, 2%, 0.7)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const drawMouseGlow = () => {
      if (mouseRef.current.x < 0) return;
      
      const gradient = ctx.createRadialGradient(
        mouseRef.current.x, mouseRef.current.y, 0,
        mouseRef.current.x, mouseRef.current.y, 350
      );
      gradient.addColorStop(0, 'hsla(0, 75%, 50%, 0.1)');
      gradient.addColorStop(0.3, 'hsla(180, 80%, 50%, 0.03)');
      gradient.addColorStop(0.6, 'hsla(0, 75%, 50%, 0.02)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const animate = (time: number) => {
      timeRef.current = time;
      ctx.fillStyle = 'hsl(0, 5%, 4%)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawCircuitLines(time);
      drawDataStreams();
      drawParticles();
      drawPulses();
      drawScanLine(time);
      drawMouseGlow();
      drawVignette();

      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleClick = (e: MouseEvent) => {
      createPulse(e.clientX, e.clientY);
    };

    resize();
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.9 }}
    />
  );
};
